import React, { useCallback, useEffect, useState } from 'react'
import { Box } from 'components/Layout'
import TextInput from 'components/TextInput'
import { useToaster } from 'utils/useToaster'
import { SelectInput } from 'components/SelectInput'
import { displayText } from 'sharedComponents/Insurance/InsuranceImage'
import { Upload } from 'components/Upload'
import {
  useInsuranceForm,
  InsuranceField,
  getErrorMessage,
  useEditEventAttendeeInsurance,
  EventAttendee_eventAttendee_Payment,
  RELATIONSHIPS,
  INSURANCE_PROVIDER,
  Gender,
} from '@modmd/data'
import Button from 'components/Button'
import Card from 'components/Card'
import { COLOR, pxToRem, fontSizes } from 'theme'
import { Text } from 'components/Typography'
import Label from 'components/Label'
import { DateInput } from 'components/DateInput'

interface FormInsuranceProps {
  userId: string
  onClose?: () => void
  refetchInsurance?: () => void
  insurances: EventAttendee_eventAttendee_Payment | null
  isDialog?: boolean
  isInsurance?: boolean
}

type InsuranceUploadProps = {
  imgURL?: string | null
  checked?: boolean | null
  type: string
  reason?: string | null
  isError?: boolean
  onChangeFile: (fieldName: string, hashFile: string) => void
}

const InsuranceUpload = ({
  imgURL,
  checked,
  type,
  reason,
  isError,
  onChangeFile,
}: InsuranceUploadProps) => (
  <Box mb="1rem">
    <Upload
      id={type}
      label={displayText[type] || ''}
      setDefaultPreview={imgURL}
      onChangeFile={onChangeFile}
      isError={isError}
      canReUpload
    />

    {imgURL && (
      <Box>
        {checked === true && <Label indicator="green">Approved</Label>}
        {checked === null && <Label indicator="yellow">In review</Label>}
        {checked === false && (
          <>
            <Label indicator="red">Denied</Label>
            <Text>Reason: </Text>
            <Text fontSize="m">{reason}</Text>
          </>
        )}
      </Box>
    )}
  </Box>
)

export const AttendeeFormInsurance = ({
  userId,
  onClose = () => {},
  insurances,
  refetchInsurance = () => {},
  isDialog,
  isInsurance = true,
}: FormInsuranceProps) => {
  const { setToastMessage } = useToaster()

  const [editInsurance, { loading: editInsuranceLoading }] = useEditEventAttendeeInsurance()

  const [otherInsuranceCompany, setOtherInsuranceCompany] = useState(false)
  const [showOthers, setShowOthers] = useState(false)
  const [isSelf, setIsSelf] = useState(false)

  const getValidationSchema = useCallback(() => {
    if (isSelf) {
      return 'fullSelfSubscriber'
    }
    return 'full'
  }, [isSelf])

  useEffect(() => {
    setIsSelf(insurances?.relationToSubscriber === 'self')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const insuranceData = useInsuranceForm({
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      const insurance = {
        id: insurances!.id,
        govId: values.govId,
        insuranceCardBack: values.insuranceCardBack,
        insuranceCardFront: values.insuranceCardFront,
        insuranceCompany: values.insuranceCompany,
        plan: values.plan,
        cardID: values.cardID,
        groupNumber: values.groupNumber,
        relationToSubscriber: values.relationToSubscriber,
        SSN: values.SSN,
        driversLicense: values.driversLicense,
        subscriberFirstName: values.subscriberFirstName,
        subscriberMiddleName: values.subscriberMiddleName,
        subscriberLastName: values.subscriberLastName,
        subscriberDOB: values.subscriberDOB,
        subscriberGender: values.subscriberGender,
      }
      await editInsurance({
        variables: {
          inputData: insurance,
        },
      })
      setToastMessage('Data successfully changed', 'success')
      refetchInsurance()
      onClose()
    },
    initialValues: {
      ...insurances,
      subscriberGender: (insurances?.subscriberGender as Gender) || null,
      userId,
    },
  })

  const getRelationToSubscriberValue = (relationToSubscriber: string | null) => {
    if (relationToSubscriber) {
      if (
        RELATIONSHIPS.filter((relationship) => relationship.value === relationToSubscriber).length
      ) {
        return relationToSubscriber
      }
      return 'other'
    }
    return ''
  }

  const getInsuranceCompanyValue = (insuranceCompany: string | null) => {
    if (insuranceCompany) {
      if (
        INSURANCE_PROVIDER.filter(
          (insurance_provider) => insurance_provider.value === insuranceCompany
        ).length
      ) {
        return insuranceCompany
      }
      return 'Other'
    }
    return ''
  }

  const verifyShowOthers = () => {
    const relationToSubscriber = getRelationToSubscriberValue(
      insuranceData?.values.relationToSubscriber || ''
    )
    if (showOthers || relationToSubscriber === 'other') {
      return true
    }
    return false
  }

  return (
    <Box display="flex" flexDirection="column">
      {isDialog && (
        <Card.Title
          onDismiss={() => {
            onClose()
          }}
        >
          Edit Insurance information
        </Card.Title>
      )}
      <Card.Content>
        {isInsurance && (
          <>
            <SelectInput
              name={InsuranceField.INSURANCECOMPANY}
              label="Insurance Company"
              onChange={(e) => {
                insuranceData?.setFieldValue('insuranceCompany', e.target.value)
                if (e.target.value === 'Other') {
                  setOtherInsuranceCompany(true)
                } else {
                  setOtherInsuranceCompany(false)
                }
              }}
              value={getInsuranceCompanyValue(insuranceData.values?.insuranceCompany || '')}
              hasEmptyValue
              options={INSURANCE_PROVIDER}
              errorMessage={getErrorMessage(
                insuranceData?.touched.insuranceCompany,
                insuranceData?.errors.insuranceCompany
              )}
            />
            {(otherInsuranceCompany ||
              getInsuranceCompanyValue(insuranceData.values?.insuranceCompany || '') ===
                'Other') && (
              <TextInput
                name={InsuranceField.INSURANCECOMPANY}
                errorMessage={getErrorMessage(
                  insuranceData?.touched.relationToSubscriber,
                  insuranceData?.errors.relationToSubscriber
                )}
                onChange={insuranceData?.handleChange}
                onBlur={insuranceData?.handleBlur}
                label="Insurance Company"
                placeholder=""
                hasBottomContent
                value={insuranceData.values?.insuranceCompany || ''}
                isFullWidth
              />
            )}
            <TextInput
              name={InsuranceField.PLAN}
              value={insuranceData?.values.plan || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={getErrorMessage(
                insuranceData?.touched.plan,
                insuranceData?.errors.plan
              )}
              label="Plan"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
            <TextInput
              name={InsuranceField.CARDID}
              value={insuranceData?.values.cardID || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={getErrorMessage(
                insuranceData?.touched.cardID,
                insuranceData?.errors.cardID
              )}
              label="ID #"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
            <TextInput
              name={InsuranceField.GROUPNUMBER}
              value={insuranceData?.values.groupNumber || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={getErrorMessage(
                insuranceData?.touched.groupNumber,
                insuranceData?.errors.groupNumber
              )}
              label="Group #"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
            <TextInput
              name={InsuranceField.SUBSCRIBER_NAME}
              value={insuranceData?.values.subscriberFirstName || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={
                insuranceData?.values.relationToSubscriber !== 'self'
                  ? getErrorMessage(
                      insuranceData?.touched.subscriberFirstName,
                      insuranceData?.errors.subscriberFirstName
                    )
                  : ''
              }
              label="Subscriber First Name"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
            <TextInput
              name={InsuranceField.SUBSCRIBER_MIDDLE_NAME}
              value={insuranceData?.values.subscriberMiddleName || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={
                insuranceData?.values.relationToSubscriber !== 'self'
                  ? getErrorMessage(
                      insuranceData?.touched.subscriberMiddleName,
                      insuranceData?.errors.subscriberMiddleName
                    )
                  : ''
              }
              label="Subscriber Middle Name (Optional)"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
            <TextInput
              name={InsuranceField.SUBSCRIBER_LAST_NAME}
              value={insuranceData?.values.subscriberLastName || ''}
              onChange={insuranceData?.handleChange}
              onBlur={insuranceData?.handleBlur}
              errorMessage={
                insuranceData?.values.relationToSubscriber !== 'self'
                  ? getErrorMessage(
                      insuranceData?.touched.subscriberLastName,
                      insuranceData?.errors.subscriberLastName
                    )
                  : ''
              }
              label="Subscriber Last Name"
              placeholder=""
              hasBottomContent
              isFullWidth
            />
          </>
        )}
        <TextInput
          name={InsuranceField.SSN}
          value={insuranceData?.values.SSN || ''}
          onChange={insuranceData?.handleChange}
          onBlur={insuranceData?.handleBlur}
          errorMessage={getErrorMessage(insuranceData?.touched.SSN, insuranceData?.errors.SSN)}
          label="SSN (Optional)"
          placeholder=""
          hasBottomContent
          isFullWidth
        />
        <TextInput
          name={InsuranceField.DRIVERLICENSE}
          value={insuranceData?.values.driversLicense || ''}
          onChange={insuranceData?.handleChange}
          onBlur={insuranceData?.handleBlur}
          errorMessage={getErrorMessage(
            insuranceData?.touched.driversLicense,
            insuranceData?.errors.driversLicense
          )}
          label="Government-Issued Id Card #"
          placeholder=""
          hasBottomContent
          isFullWidth
        />
        <SelectInput
          name={InsuranceField.RELATIONTOSUBSCRIBER}
          label="Relationship to subscriber"
          onChange={(e) => {
            insuranceData?.setFieldValue('relationToSubscriber', e.target.value)
            if (e.target.value === 'other') {
              setShowOthers(true)
              setIsSelf(false)
            } else if (e.target.value === 'self') {
              setShowOthers(false)
              setIsSelf(true)
            } else {
              setShowOthers(false)
              setIsSelf(false)
            }
          }}
          value={getRelationToSubscriberValue(insuranceData?.values.relationToSubscriber || '')}
          hasEmptyValue
          options={RELATIONSHIPS}
          errorMessage={getErrorMessage(
            insuranceData?.touched.relationToSubscriber,
            insuranceData?.errors.relationToSubscriber
          )}
        />
        {verifyShowOthers() && (
          <TextInput
            name={InsuranceField.RELATIONTOSUBSCRIBER}
            errorMessage={getErrorMessage(
              insuranceData?.touched.relationToSubscriber,
              insuranceData?.errors.relationToSubscriber
            )}
            onChange={insuranceData?.handleChange}
            onBlur={insuranceData?.handleBlur}
            label="Relationship to subscriber"
            placeholder=""
            hasBottomContent
            value={insuranceData?.values.relationToSubscriber || ''}
            isFullWidth
          />
        )}
        {insuranceData.values.relationToSubscriber &&
          insuranceData.values.relationToSubscriber !== 'self' && (
            <>
              <DateInput
                id="birthdate"
                name={InsuranceField.SUBSCRIBER_DOB}
                label="Subscriber Birthdate"
                value={
                  insuranceData.values.subscriberDOB
                    ? new Date(insuranceData.values.subscriberDOB)
                    : null
                }
                onChange={(date) => {
                  void insuranceData.setFieldValue('subscriberDOB', date || null)
                }}
                errorMessage={getErrorMessage(
                  insuranceData.touched.subscriberDOB,
                  insuranceData.errors.subscriberDOB
                )}
                isFullWidth
              />

              <SelectInput
                name={InsuranceField.SUBSCRIBER_GENDER}
                label="Subscriber Gender"
                onChange={(e) => {
                  insuranceData?.setFieldValue('subscriberGender', e.target.value)
                }}
                value={insuranceData?.values?.subscriberGender || ''}
                hasEmptyValue
                options={[
                  { value: '', label: 'Choose one', disabled: true },
                  { value: Gender.Male, label: 'Male' },
                  {
                    value: Gender.Female,
                    label: 'Female',
                  },
                  {
                    value: Gender.Other,
                    label: 'Other',
                  },
                  {
                    value: Gender.DeclineToState,
                    label: 'Decline to state',
                  },
                ]}
                errorMessage={getErrorMessage(
                  insuranceData?.touched.subscriberGender,
                  insuranceData?.errors.subscriberGender
                )}
              />
            </>
          )}
        <InsuranceUpload
          type="govId"
          onChangeFile={(fieldName, hashFile) => {
            void insuranceData.setFieldValue(fieldName, hashFile)
            void insuranceData.setFieldValue('govIdChecked', null)
          }}
          isError={!!insuranceData.errors.govId && insuranceData.touched.govId}
          imgURL={insuranceData?.values?.govId}
          checked={insuranceData?.values?.govIdChecked}
          reason={insuranceData?.values?.govIdMessageDenied}
        />
        {isInsurance && (
          <>
            <InsuranceUpload
              type="insuranceCardFront"
              onChangeFile={(fieldName, hashFile) => {
                void insuranceData.setFieldValue(fieldName, hashFile)
                void insuranceData.setFieldValue('insuranceCardFrontChecked', null)
              }}
              isError={
                !!insuranceData.errors.insuranceCardFront &&
                insuranceData.touched.insuranceCardFront
              }
              imgURL={insuranceData?.values?.insuranceCardFront}
              checked={insuranceData?.values?.insuranceCardFrontChecked}
              reason={insuranceData?.values?.insuranceCardFrontMessageDenied}
            />
            <InsuranceUpload
              type="insuranceCardBack"
              onChangeFile={(fieldName, hashFile) => {
                void insuranceData.setFieldValue(fieldName, hashFile)
                void insuranceData.setFieldValue('insuranceCardBackChecked', null)
              }}
              isError={
                !!insuranceData.errors.insuranceCardBack && insuranceData.touched.insuranceCardBack
              }
              imgURL={insuranceData?.values?.insuranceCardBack}
              checked={insuranceData?.values?.insuranceCardBackChecked}
              reason={insuranceData?.values?.insuranceCardBackMessageDenied}
            />
          </>
        )}

        <Box display="flex" flexDirection="column">
          <Text color={COLOR.grayDark} fontSize={fontSizes.xs}>
            *not all browsers accept HEIC natively and that&apos;s why you may not be able to upload
            it
          </Text>
          <Button
            isFetching={editInsuranceLoading}
            onClick={() => insuranceData?.handleSubmit()}
            mt={pxToRem(16)}
          >
            Save Insurance Data
          </Button>
        </Box>
      </Card.Content>
    </Box>
  )
}
